/**
 * HTTP client.
 *
 * One place that knows about base URLs, auth headers, error envelopes and
 * token refresh. Screens never call `fetch`, and components never call this —
 * they go through the hooks in `hooks.ts`.
 */

import type { ApiError } from '@mfa/domain';

import { config } from '../../app/config';
import { tokenStore } from '../auth/tokenStore';
import { handle as handleDemo } from './demo/demoBackend';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors?: Record<string, string[]>;
  readonly requestId?: string;

  constructor(status: number, body: Partial<ApiError>) {
    super(body.message || 'Something went wrong. Please try again.');
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body.code ?? 'server.error';
    this.fieldErrors = body.fieldErrors;
    this.requestId = body.requestId;
  }

  /** True when retrying the same request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  /** Field errors flattened to one message per field, ready for a form. */
  get fieldMessages(): Record<string, string> {
    if (!this.fieldErrors) return {};
    return Object.fromEntries(
      Object.entries(this.fieldErrors).map(([field, messages]) => [field, messages[0] ?? '']),
    );
  }
}

export class NetworkError extends ApiRequestError {
  constructor() {
    super(0, {
      code: 'network.unavailable',
      message: 'No connection. Check your network and try again.',
    });
    this.name = 'NetworkError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Send the access token. Defaults to true when one is available. */
  auth?: boolean;
  /**
   * Extra headers, resolved by the caller.
   *
   * This is how the installation credential travels. The transport does not
   * import the identity module: that would be a cycle, and more to the point
   * the thing that moves bytes has no business knowing who the caller is.
   */
  headers?: () => Promise<Record<string, string>>;
  signal?: AbortSignal;
  /** Deduplicates a retried write on the server. */
  idempotencyKey?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Join the base URL, the path and the query.
 *
 * Built by hand rather than with `URL`/`URLSearchParams`. React Native ships a
 * partial polyfill whose `URLSearchParams.set` throws "not implemented", so
 * every request that carried a query — My Donations, its filters and sorting,
 * paginated lists — failed on iOS and Android with what looked like a network
 * error. The browser and the test environment have the real thing, so nothing
 * caught it; a device did.
 */
function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = config.apiBaseUrl.replace(/\/$/, '');
  let url = `${base}/${path.replace(/^\//, '')}`;

  if (query) {
    const pairs: string[] = [];
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
    if (pairs.length > 0) {
      url += `${url.includes('?') ? '&' : '?'}${pairs.join('&')}`;
    }
  }

  return url;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Refresh the session, collapsing concurrent attempts into one.
 *
 * Without this, several screens hitting a 401 at once would each spend the
 * refresh token; only the first would succeed and the rest would log the
 * donor out.
 */
async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = await tokenStore.getRefreshToken();
    if (!refreshToken) return false;
    try {
      const response = await fetch(buildUrl('auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        await tokenStore.clear();
        return false;
      }
      const tokens = await response.json();
      await tokenStore.save(tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Answer from bundled data instead of the network.
 *
 * Kept behind the same `execute` seam as a real request, so hooks, error
 * handling and screens are identical in both modes — the demo exercises the
 * real app, not a parallel one.
 */
async function executeDemo<T>(path: string, options: RequestOptions): Promise<T> {
  const { method = 'GET', body, query } = options;
  // A touch of latency so loading states are visible rather than skipped.
  await new Promise((resolve) => setTimeout(resolve, 120));
  try {
    return handleDemo({ method, path: path.replace(/^\//, ''), body, query }) as T;
  } catch (error) {
    const e = error as Error & { status?: number; code?: string };
    throw new ApiRequestError(e.status ?? 500, { code: e.code, message: e.message });
  }
}

async function execute<T>(path: string, options: RequestOptions, retrying = false): Promise<T> {
  if (config.demoMode) {
    return executeDemo<T>(path, options);
  }

  const {
    method = 'GET',
    body,
    auth = true,
    signal,
    idempotencyKey,
    query,
    headers: extraHeaders,
  } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  if (extraHeaders) {
    Object.assign(headers, await extraHeaders());
  }

  if (auth) {
    const token = await tokenStore.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch {
    throw new NetworkError();
  }

  if (response.status === 401 && auth && !retrying) {
    if (await refreshSession()) {
      return execute<T>(path, options, true);
    }
    await tokenStore.clear();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const raw = await response.text();
  const payload = raw ? safeParse(raw) : {};

  if (!response.ok) {
    throw new ApiRequestError(response.status, payload as Partial<ApiError>);
  }
  return payload as T;
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return { message: raw };
  }
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    execute<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    execute<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    execute<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    execute<T>(path, { ...options, method: 'DELETE' }),
};
